/**
 * ConnectedIngredients — the expanded `חלופה 1..n` list of an ingredient
 * family (the ingredient itself + its `connectedIngredients` substitutes).
 *
 * Collapsed by default; IngredientRow owns the toggle and renders the single
 * active option. This component only appears once the coach opens the list.
 *
 * Rules ported from doof:
 *   - `חלופה 1` IS the main ingredient and can never be deleted here
 *     (deleting the main one from the row promotes the first substitute).
 *   - Substitutes never nest — a substitute has no substitutes of its own.
 */

import { Check, Info, Trash } from "@phosphor-icons/react";

import { GRAM_UNIT, calcIngredientNutrition } from "../../../utils/nutritionCalc";
import { MACROS, MISSING_AMOUNT_HINT, fmtNum, unitsForOption } from "./IngredientRow";
import NmbIconButton from "./NmbIconButton";
import "./MenuCards.css";

const ConnectedIngredients = ({
  options,
  activeIndex,
  isExpanded,
  errors,
  onSetActive,
  onOptionChange,
  onDeleteOption,
}) => {
  return (
    <div className={`nmb-options ${isExpanded ? "open" : ""}`}>
      <div className="nmb-options-inner">
        <span className="nmb-options-title">החלופות של הפריט — הלקוח/ה בוחר/ת אחת מהן</span>

        {options.map((option, optionIndex) => {
          const values = calcIngredientNutrition(option);
          const missingAmount = !Number(option.amount);
          const isActive = optionIndex === activeIndex;
          const isCustomFood = option.foodType === "customFood";
          const error = errors[`ing:${option.ingredientId}`];

          return (
            <div
              key={option.ingredientId || `option-${optionIndex}`}
              className={`nmb-option ${isActive ? "active" : ""} ${error ? "invalid" : ""}`}
              data-nmb-key={`ing:${option.ingredientId}`}
            >
              <div className="nmb-option-head">
                <span className="nmb-badge nmb-badge-alt">חלופה {optionIndex + 1}</span>
                <span className="nmb-option-name" title={option.foodName}>
                  {option.foodName}
                </span>
                {missingAmount && (
                  <span
                    className="nmb-missing-hint"
                    title={MISSING_AMOUNT_HINT}
                    aria-label={MISSING_AMOUNT_HINT}
                  >
                    <Info size={11} weight="fill" />
                  </span>
                )}
              </div>

              <div className="nmb-option-controls">
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  dir="ltr"
                  className={`nmb-amount-input ${missingAmount ? "missing" : ""}`}
                  value={option.amount ?? 0}
                  onChange={(event) =>
                    onOptionChange(optionIndex, { amount: event.target.value })
                  }
                  aria-label={`כמות עבור חלופה ${optionIndex + 1}`}
                />

                {isCustomFood ? (
                  <span className="nmb-unit-static">מנות</span>
                ) : (
                  <select
                    className="nmb-unit-select"
                    value={option.selectedAmount || GRAM_UNIT}
                    onChange={(event) =>
                      onOptionChange(optionIndex, { selectedAmount: event.target.value })
                    }
                    aria-label={`יחידת מידה עבור חלופה ${optionIndex + 1}`}
                  >
                    {unitsForOption(option).map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </select>
                )}

                <button
                  type="button"
                  className={`nmb-option-pick ${isActive ? "active" : ""}`}
                  onClick={() => onSetActive(optionIndex)}
                  aria-pressed={isActive}
                >
                  {isActive ? <Check size={12} weight="bold" /> : null}
                  <span>{isActive ? "מוצג בתפריט" : "הצגה בתפריט"}</span>
                </button>

                {optionIndex > 0 && (
                  <NmbIconButton
                    label={`מחיקת חלופה ${optionIndex + 1}`}
                    variant="danger"
                    onClick={() => onDeleteOption(optionIndex)}
                  >
                    <Trash size={13} weight="fill" />
                  </NmbIconButton>
                )}
              </div>

              <div className="nmb-values-row">
                {MACROS.map((macro) => (
                  <span className="nmb-chip" key={macro.key}>
                    {macro.label} {fmtNum(values[macro.key])}
                  </span>
                ))}
              </div>

              {error && <p className="nmb-card-error">{error}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ConnectedIngredients;
